/**
 * Mass → wing-loading model. Turns the two physical sliders
 * (pilot weight + water ballast) into the quantities the polar maths consume:
 *
 *   total mass   = empty + pilot + water           (kg)
 *   wing loading = total mass / wing area           (kg/m²)  — the slider readout
 *   k            = √(total mass / reference mass)            — fed to effectiveCoeffs
 *
 * Pure; the glider supplies empty mass, wing area and reference mass.
 */
import type { Glider } from "./glider";

/**
 * Assumed reference-pilot mass used only to reconstruct an empty mass when a
 * glider record lacks one. Every record in the current database carries
 * `emptyMass_kg`, so this fallback is a type-safety belt rather than a live path.
 */
const FALLBACK_REFERENCE_PILOT_KG = 75;

export const emptyMass_kg = (glider: Glider): number =>
  glider.emptyMass_kg ??
  Math.max(0, glider.referenceMass_kg - FALLBACK_REFERENCE_PILOT_KG);

export const totalMass_kg = (
  glider: Glider,
  pilot_kg: number,
  water_kg: number,
): number => emptyMass_kg(glider) + pilot_kg + water_kg;

/** Wing loading in kg/m² for the given pilot + water load. */
export const wingLoading_kg_m2 = (
  glider: Glider,
  pilot_kg: number,
  water_kg: number,
): number => totalMass_kg(glider, pilot_kg, water_kg) / glider.wingArea_m2;

/** k = √(m_total / m_ref) — the wing-loading factor `effectiveCoeffs` expects. */
export const loadingFactor = (
  glider: Glider,
  pilot_kg: number,
  water_kg: number,
): number =>
  Math.sqrt(totalMass_kg(glider, pilot_kg, water_kg) / glider.referenceMass_kg);
